import { CreativeTagEvent } from '../enums';
import {
  PayloadBuilderStrategy,
  PayloadResult
} from './payload-builder-strategy';
import { eventByTrackingMap } from './event-by-tracking-map';
import {
  EventRedirectContext,
  CreativeTagTrackingPayload,
  PayloadDatas,
  MacrosContext,
  ContainerDimensions,
  CreativeDimensions
} from '../types';
import {
  ContextParameterName,
  ParametersExtractor
} from '../parameters-extractor/parameters-extractor';
import { ContainerDimensionRetriever } from '../container-dimension-retriever/container-dimension-retriever';
import {
  CREATIVE_CONTEXT_MACRO_SOURCE,
  resolveMacro,
  resolveMacroWithSource
} from './resolve-macro';

const DEFAULT_PLAYER_VERSION = -1;
const DEFAULT_CALLBACKS_VERSION = -1;
const DEFAULT_TAG_VERSION = '-1';

export class PayloadBuilderRedirectStrategy implements PayloadBuilderStrategy {
  private creativeInitTimestamp: number = 0;

  constructor(
    private readonly containerDimensionsRetriever: ContainerDimensionRetriever,
    private readonly parameterExtractor: ParametersExtractor
  ) {
    this.creativeInitTimestamp = Date.now();
  }

  setInitTime(timestamp: number): void {
    this.creativeInitTimestamp = timestamp;
  }

  buildPayload(
    creativeTagEvent: CreativeTagEvent,
    eventRedirectContext?: PayloadDatas & {
      eventContext: EventRedirectContext;
    },
    evt?: CustomEvent
  ): PayloadResult {
    const experience = eventRedirectContext?.experience;
    const eventContext = this.getEventContext();

    const ttr =
      creativeTagEvent === CreativeTagEvent.CreativeAssetLoad
        ? Date.now() - this.creativeInitTimestamp
        : undefined;

    const creativeTrackingPayload: CreativeTagTrackingPayload = {
      event: eventByTrackingMap.get(creativeTagEvent),
      advert_id: eventContext.macrosContext.advertId,
      creative_id: eventContext.macrosContext.creativeId,
      campaign_id: eventContext.macrosContext.campaignId,
      template: experience ? experience.metadata.templateId : 'image',
      ...(ttr ? { ttr } : {}),
      site_id: eventContext.adsSetId,
      execution_environment: eventContext.executionEnvironment || 'ogury',
      is_creative_tag: true,
      ...(experience && {
        player_version: DEFAULT_PLAYER_VERSION,
        callbacks_version: DEFAULT_CALLBACKS_VERSION,
        template_version: String(experience.metadata.templateVersion),
        experience_id: String(experience.parameters.experienceId),
        container_policy: experience.metadata.containerPolicy
      }),
      details: {
        ...(evt?.detail?.timespent_seconds !== undefined && {
          timespent_seconds: evt.detail.timespent_seconds
        }),
        creative_width: eventContext.creativeDimensions.creative_width,
        creative_height: eventContext.creativeDimensions.creative_height,
        container_width: eventContext.containerDimensions.container_width,
        container_height: eventContext.containerDimensions.container_height,
        ...(creativeTagEvent !== CreativeTagEvent.CreativeInit && {
          time_from_init: Date.now() - this.creativeInitTimestamp
        }),
        tag_version: DEFAULT_TAG_VERSION,
        ...(eventContext.macrosContext.fromCreativeContext &&
          creativeTagEvent === CreativeTagEvent.CreativeAssetLoad && {
            macro_source: CREATIVE_CONTEXT_MACRO_SOURCE
          })
      }
    };
    return creativeTrackingPayload;
  }

  private getEventContext(): EventRedirectContext {
    const macros = this.parameterExtractor.getContextParameters<{
      [key: string]: string;
    }>('macros', window);

    const executionEnvironment = this.parameterExtractor.getContextParameters(
      'executionEnvironment',
      window
    ) as string;

    const eventRedirectContext: EventRedirectContext = {
      macrosContext: this.getMacrosContext(macros),
      adsSetId: this.getContextParameter('adsSetId'),
      executionEnvironment,
      containerDimensions: this.getContainerDimensions(macros),
      creativeDimensions: this.getCreativeDimensions(macros)
    };
    return eventRedirectContext;
  }

  private getMacrosContext(macros: { [key: string]: string }): MacrosContext {
    const campaignId = resolveMacroWithSource(
      'campaignid',
      macros.campaignid,
      this.parameterExtractor
    );
    const advertId = resolveMacroWithSource(
      'advert_id',
      macros.advert_id,
      this.parameterExtractor
    );
    return {
      campaignId: campaignId.value,
      advertId: advertId.value,
      creativeId: resolveMacro(
        'creative_id',
        macros.creative_id,
        this.parameterExtractor
      ),
      fromCreativeContext: advertId.fromCreativeContext
    };
  }

  private getContainerDimensions(macros: {
    [key: string]: string;
  }): ContainerDimensions {
    const {
      width: container_width,
      height: container_height
    } = this.containerDimensionsRetriever.getContainerDimensions(
      window,
      macros
    );
    return { container_width, container_height };
  }

  private getCreativeDimensions(macros: {
    [key: string]: string;
  }): CreativeDimensions {
    return {
      creative_width: parseInt(macros.creative_size?.split('x')[0]),
      creative_height: parseInt(macros.creative_size?.split('x')[1])
    };
  }

  private getContextParameter(key: ContextParameterName): any {
    return this.parameterExtractor.getContextParameters(key, window);
  }
}
